import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircleQuestion, Camera, RotateCcw, Loader2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

import API from "@/apiConfig";

export function FeedbackButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", trying_to_do: "", what_happened: "" });
  const isCapturingRef = useRef(false);

  useEffect(() => {
    axios.get(`${API}/feedback/settings`).then(r => setEnabled(r.data.enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    if (open && !isCapturingRef.current) {
      const profile = JSON.parse(localStorage.getItem("wagc_profile") || "{}");
      setForm(f => ({
        ...f,
        name: f.name || profile.display_name || profile.name || "",
        email: f.email || profile.email || "",
        phone: f.phone || profile.phone || "",
      }));
      setScreenshot(null);
    }
  }, [open]);

  const captureScreenshot = async () => {
    isCapturingRef.current = true;
    setCapturing(true);
    setOpen(false);
    await new Promise(r => setTimeout(r, 400));
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        scale: 0.7,
        logging: false,
        ignoreElements: (el) => el.dataset?.testid === "feedback-fab",
      });
      setScreenshot(canvas.toDataURL("image/png", 0.6));
    } catch (e) {
      toast.error("Could not capture screenshot");
    }
    setCapturing(false);
    setOpen(true);
    setTimeout(() => { isCapturingRef.current = false; }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.trying_to_do.trim() && !form.what_happened.trim()) { toast.error("Please describe what you were doing or what happened"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        page: window.location.pathname,
        trying_to_do: form.trying_to_do,
        what_happened: form.what_happened,
        screenshot_base64: screenshot || null,
      });
      toast.success("Thank you! We've received your report and will look into it soon.");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", trying_to_do: "", what_happened: "" });
      setScreenshot(null);
    } catch {
      toast.error("Failed to send feedback. Please try again.");
    }
    setSubmitting(false);
  };

  if (!enabled) return null;

  return (
    <>
      <button
        data-testid="feedback-fab"
        onClick={() => setOpen(true)}
        className="fixed bottom-20 lg:bottom-6 right-4 z-40 bg-navy text-gold rounded-full w-12 h-12 flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
        aria-label="Send feedback"
      >
        <MessageCircleQuestion className="h-5 w-5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="text-navy text-lg">Help us make the app better</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              We're still getting things just right. Tell us what's happening so we can fix it quickly.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="fb-name">Name *</Label>
                <Input id="fb-name" data-testid="feedback-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your name" required />
              </div>
              <div>
                <Label htmlFor="fb-email">Email</Label>
                <Input id="fb-email" data-testid="feedback-email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="you@example.com" />
              </div>
            </div>

            <div>
              <Label htmlFor="fb-phone">Phone (optional)</Label>
              <Input id="fb-phone" data-testid="feedback-phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(555) 555-5555" />
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Page</Label>
              <p className="text-sm font-mono bg-muted px-2 py-1 rounded" data-testid="feedback-page">{location.pathname}</p>
            </div>

            <div>
              <Label htmlFor="fb-trying">What are you trying to do?</Label>
              <Textarea id="fb-trying" data-testid="feedback-trying" rows={2} value={form.trying_to_do} onChange={e => setForm(f => ({ ...f, trying_to_do: e.target.value }))} placeholder="e.g., I was trying to join a Collective" />
            </div>

            <div>
              <Label htmlFor="fb-happened">What is happening instead?</Label>
              <Textarea id="fb-happened" data-testid="feedback-happened" rows={2} value={form.what_happened} onChange={e => setForm(f => ({ ...f, what_happened: e.target.value }))} placeholder="e.g., nothing happened when I clicked Join" />
            </div>

            <div>
              <Label className="mb-1 block">Screenshot (optional)</Label>
              {screenshot ? (
                <div className="relative border rounded overflow-hidden">
                  <img src={screenshot} alt="Screenshot preview" className="w-full max-h-40 object-contain bg-muted" data-testid="feedback-screenshot-preview" />
                  <div className="absolute top-1 right-1 flex gap-1">
                    <Button type="button" size="icon" variant="secondary" className="h-7 w-7" onClick={captureScreenshot} title="Retake">
                      <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                    <Button type="button" size="icon" variant="secondary" className="h-7 w-7" onClick={() => setScreenshot(null)} title="Remove">
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ) : (
                <Button type="button" variant="outline" size="sm" onClick={captureScreenshot} disabled={capturing} data-testid="feedback-capture-btn">
                  {capturing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Camera className="h-4 w-4 mr-1" />}
                  {capturing ? "Capturing..." : "Capture Screen"}
                </Button>
              )}
            </div>

            <Button type="submit" className="w-full bg-navy text-gold hover:bg-navy/90" disabled={submitting} data-testid="feedback-submit-btn">
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</> : "Send Feedback"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
